import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTokens } from '../../contexts/TokenContext';
import { TOKEN_COSTS, TOKEN_EARNINGS, UnlockableFeature } from '../../types/tokens';
import './FreeLimitModal.css';

export type LimitType =
  | 'daily_courses'      // 每日課程觀看上限
  | 'vocabulary_save'    // 生字本儲存上限
  | 'notes'              // 筆記功能
  | 'dictation'          // 聽寫模式
  | 'shadowing'          // 跟讀模式
  | 'quiz'               // 測驗模式
  | 'download'           // 離線下載
  | 'ad_free';           // 無廣告

interface FreeLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  limitType: LimitType;
  currentUsage?: number;
  maxUsage?: number;
  onUnlockSuccess?: () => void;  // Callback when unlock succeeds
}

// Map LimitType to UnlockableFeature
const limitTypeToFeature: Partial<Record<LimitType, UnlockableFeature>> = {
  daily_courses: 'course',
  dictation: 'dictation',
  shadowing: 'shadowing',
  quiz: 'quiz',
  vocabulary_save: 'vocabulary',
};

// Get token cost for a limit type
const getTokenCost = (limitType: LimitType): number | null => {
  const costMap: Partial<Record<LimitType, number>> = {
    daily_courses: TOKEN_COSTS.UNLOCK_COURSE,
    dictation: TOKEN_COSTS.UNLOCK_DICTATION,
    shadowing: TOKEN_COSTS.UNLOCK_SHADOWING,
    quiz: TOKEN_COSTS.UNLOCK_QUIZ,
    vocabulary_save: TOKEN_COSTS.UNLOCK_VOCABULARY,
  };
  return costMap[limitType] ?? null;
};

const LockIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const StarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const TokenIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 6v12M8 10l4-4 4 4M8 14l4 4 4-4"/>
  </svg>
);

const PlayIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5 3 19 12 5 21 5 3"/>
  </svg>
);

const FreeLimitModal: React.FC<FreeLimitModalProps> = ({
  isOpen,
  onClose,
  limitType,
  currentUsage,
  maxUsage,
  onUnlockSuccess,
}) => {
  const { t } = useTranslation('pricing');
  const navigate = useNavigate();
  const { stats, unlock, requestAdNonce, watchAdForTokens, canAfford } = useTokens();

  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isWatchingAd, setIsWatchingAd] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const tokenCost = getTokenCost(limitType);
  const feature = limitTypeToFeature[limitType];
  const hasEnoughTokens = tokenCost !== null && canAfford(tokenCost);
  const canWatchAd = stats && stats.dailyAdTokensRemaining > 0;

  const handleUpgradeClick = () => {
    onClose();
    navigate('/pricing');
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleUseTokens = async () => {
    if (!feature || !tokenCost) return;

    setIsUnlocking(true);
    setError(null);
    try {
      const success = await unlock(feature);
      if (success) {
        onUnlockSuccess?.();
        onClose();
      } else {
        setError(t('limits.unlockFailed'));
      }
    } catch (err) {
      setError(t('limits.unlockFailed'));
    } finally {
      setIsUnlocking(false);
    }
  };

  const handleWatchAd = async () => {
    setIsWatchingAd(true);
    setError(null);
    try {
      // Get nonce first
      const nonce = await requestAdNonce();
      // TODO: Integrate actual ad SDK here
      // For now, simulate watching an ad
      await new Promise(resolve => setTimeout(resolve, 1000));
      const result = await watchAdForTokens(nonce);
      // Show success message
      alert(t('limits.tokenEarned', { earned: result.earned, balance: result.newBalance }));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { code?: string } } };
      if (error?.response?.data?.code === 'DAILY_AD_LIMIT_REACHED') {
        setError(t('limits.adLimitReached'));
      } else {
        setError(t('limits.adFailed'));
      }
    } finally {
      setIsWatchingAd(false);
    }
  };

  // Get limit-specific content
  const getLimitTitle = () => {
    return t(`limits.${limitType}.title`);
  };

  const getLimitDescription = () => {
    if (currentUsage !== undefined && maxUsage !== undefined) {
      return t(`limits.${limitType}.description`, { current: currentUsage, max: maxUsage });
    }
    return t(`limits.${limitType}.description`);
  };

  const premiumBenefits = [
    t('limits.benefits.unlimitedCourses'),
    t('limits.benefits.allLearningModes'),
    t('limits.benefits.unlimitedVocabulary'),
    t('limits.benefits.offlineDownload'),
    t('limits.benefits.noAds'),
  ];

  return (
    <div className="free-limit-modal-overlay" onClick={handleBackdropClick}>
      <div className="free-limit-modal">
        {/* Close button */}
        <button className="free-limit-modal-close" onClick={onClose} aria-label={t('common:close')}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Lock icon */}
        <div className="free-limit-modal-icon">
          <LockIcon />
        </div>

        {/* Title */}
        <h2 className="free-limit-modal-title">{getLimitTitle()}</h2>

        {/* Description */}
        <p className="free-limit-modal-description">{getLimitDescription()}</p>

        {/* Usage indicator (if applicable) */}
        {currentUsage !== undefined && maxUsage !== undefined && (
          <div className="free-limit-modal-usage">
            <div className="usage-bar">
              <div
                className="usage-bar-fill"
                style={{ width: `${Math.min((currentUsage / maxUsage) * 100, 100)}%` }}
              />
            </div>
            <span className="usage-text">{currentUsage} / {maxUsage}</span>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="free-limit-modal-error">
            {error}
          </div>
        )}

        {/* Token Section - Only show if this limit type supports token unlock */}
        {tokenCost !== null && stats && (
          <div className="free-limit-modal-token-section">
            <div className="token-balance">
              <TokenIcon />
              <span>{t('limits.yourTokens')}: <strong>{stats.balance}</strong></span>
            </div>

            <div className="token-actions">
              {/* Use Tokens Button */}
              <button
                className={`token-unlock-btn ${hasEnoughTokens ? 'can-afford' : 'cannot-afford'}`}
                onClick={handleUseTokens}
                disabled={!hasEnoughTokens || isUnlocking}
              >
                {isUnlocking ? (
                  <span className="btn-loading">...</span>
                ) : (
                  <>
                    <TokenIcon />
                    <span>
                      {hasEnoughTokens
                        ? `${t('limits.useTokens')} ${tokenCost} ${t('limits.tokens')}`
                        : `${t('limits.needMore')} ${tokenCost - stats.balance} ${t('limits.moreTokens')}`
                      }
                    </span>
                  </>
                )}
              </button>

              {/* Watch Ad Button */}
              <button
                className="watch-ad-btn"
                onClick={handleWatchAd}
                disabled={!canWatchAd || isWatchingAd}
              >
                {isWatchingAd ? (
                  <span className="btn-loading">...</span>
                ) : (
                  <>
                    <PlayIcon />
                    <span>
                      {canWatchAd
                        ? `${t('limits.watchAd')} +${TOKEN_EARNINGS.AD_REWARD}`
                        : t('limits.adLimitReached')
                      }
                    </span>
                  </>
                )}
              </button>
            </div>

            <p className="token-hint">
              {t('limits.adRemaining', { count: stats.dailyAdTokensRemaining })}
            </p>
          </div>
        )}

        <div className="free-limit-modal-divider">
          <span>{t('limits.or')}</span>
        </div>

        {/* Premium benefits */}
        <div className="free-limit-modal-benefits">
          <div className="benefits-header">
            <StarIcon />
            <span>{t('limits.premiumIncludes')}</span>
          </div>
          <ul className="benefits-list">
            {premiumBenefits.map((benefit, index) => (
              <li key={index}>
                <CheckIcon />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA buttons */}
        <div className="free-limit-modal-actions">
          <button className="upgrade-btn" onClick={handleUpgradeClick}>
            {t('limits.upgradeNow')}
          </button>
          <button className="later-btn" onClick={onClose}>
            {t('limits.maybeLater')}
          </button>
        </div>

        {/* Special offer badge */}
        <div className="free-limit-modal-offer">
          {t('limits.specialOffer')}
        </div>
      </div>
    </div>
  );
};

export default FreeLimitModal;
